import React, { useState, useEffect } from 'react';
import { 
  EnvelopeIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

const EmailTracking = () => {
  const [emailStats, setEmailStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');

  useEffect(() => {
    fetchEmailStats();
  }, [timeRange]);

  const fetchEmailStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/platform/communications/email/stats?range=${timeRange}`);
      setEmailStats(response.data);
    } catch (error) {
      console.error('Error fetching email stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'bounced':
        return <XCircleIcon className="h-4 w-4 text-red-500" />;
      case 'failed':
        return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />;
      case 'pending':
        return <ClockIcon className="h-4 w-4 text-yellow-500" />;
      default:
        return <EnvelopeIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatPercentage = (value) => {
    return value ? `${value.toFixed(1)}%` : '0.0%';
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center space-x-4">
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="1d">Last 24 Hours</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
        </select>
      </div>

      {/* Email Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <EnvelopeIcon className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Sent</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {emailStats?.totalSent?.toLocaleString() || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Delivered</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {emailStats?.delivered?.toLocaleString() || 0}
                  </dd>
                  <dd className="text-sm text-green-600">
                    {formatPercentage(emailStats?.deliveryRate)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <XCircleIcon className="h-8 w-8 text-red-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Bounced</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {emailStats?.bounced?.toLocaleString() || 0}
                  </dd>
                  <dd className="text-sm text-red-600">
                    {formatPercentage(emailStats?.bounceRate)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-8 w-8 text-yellow-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Failed</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {emailStats?.failed?.toLocaleString() || 0}
                  </dd>
                  <dd className="text-sm text-yellow-600">
                    {formatPercentage(emailStats?.failureRate)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SendGrid Integration Status */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">SendGrid Integration</h3>
        </div>
        <div className="p-6">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-green-400 rounded-full"></div>
            <span className="text-sm font-medium text-gray-900">Connected</span>
            <span className="text-sm text-gray-500">
              API Key configured and webhooks active
            </span>
          </div>
          
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-500">API Rate Limit:</span>
              <span className="ml-2 font-medium">100 req/sec</span>
            </div>
            <div>
              <span className="text-gray-500">Daily Quota:</span>
              <span className="ml-2 font-medium">10,000 emails</span>
            </div>
            <div>
              <span className="text-gray-500">Used Today:</span>
              <span className="ml-2 font-medium">{emailStats?.usedToday || 0} emails</span>
            </div>
          </div>
        </div>
      </div>

      {/* Email Activity Feed */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Email Activity</h3>
        </div>
        <div className="p-6">
          <div className="text-center py-8">
            <EnvelopeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Email Activity Monitor</h3>
            <p className="mt-1 text-sm text-gray-500">
              Real-time email sending activity and status updates across all tenants.
            </p>
            <div className="mt-4 text-xs text-gray-400 space-y-1">
              <p>• Live delivery status tracking</p>
              <p>• Bounce and failure notifications</p>
              <p>• Per-tenant email analytics</p>
              <p>• Template usage statistics</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tenant Email Usage */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Email Usage by Tenant</h3>
        </div>
        <div className="p-6">
          <div className="text-center py-8">
            <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Tenant Usage Analytics</h3>
            <p className="mt-1 text-sm text-gray-500">
              Detailed breakdown of email usage, delivery rates, and trends per tenant.
            </p>
            <div className="mt-4 text-xs text-gray-400 space-y-1">
              <p>• Email volume by tenant</p>
              <p>• Delivery success rates</p>
              <p>• Template usage patterns</p>
              <p>• Cost analysis and billing</p>
            </div>
          </div>
        </div>
      </div>

      {/* SMS Integration Placeholder */}
      <div className="bg-white shadow rounded-lg border-2 border-dashed border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-500">SMS Integration (Coming Soon)</h3>
        </div>
        <div className="p-6">
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center mx-auto">
              <span className="text-gray-500 font-semibold">SMS</span>
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-500">Twilio SMS Integration</h3>
            <p className="mt-1 text-sm text-gray-400">
              SMS tracking and analytics will be available when Twilio integration is implemented.
            </p>
          </div>
        </div>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div>
            <span className="text-gray-700">Loading email statistics...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTracking;